import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ProgramProgressCell, ProgramProgressCellUpsert } from '../types/domain';
import { upsertProgramProgressCells } from '../services/programProgressService';
import { buildAnnualCellKey } from '../utils/annualTimetable';
import type { ProgramProgressMondayIsoRange } from '../utils/programProgressMondayRange';
import {
  filterProgramProgressCellsByMondayIsoInclusive,
  loadProgramProgressCellsCache,
} from '../utils/programProgressCellsCache';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import { invalidateProgramProgressCellsQuery, setProgramProgressCellsQueryData } from '../query/cacheUpdates';
import { schoolKeys } from '../query/keys';
import { useProgramProgressCellsQuery } from '../query/hooks/useProgramProgressCellsQuery';

/** 설정 시 해당 월요일 구간만 로드. 전량 캐시가 있으면 서비스에서 캐시만 슬라이스하고, 저장 후 `refreshKey`로만 강제 갱신합니다. */
export type ProgramProgressMondayRange = ProgramProgressMondayIsoRange;

interface UseProgramProgressCellsParams {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2 | null;
  gradeId: string;
  refreshKey?: number;
  /** 전량 캐시 대신 구간만 가져올 때(기본 null = 기존 동작) */
  mondayIsoRange?: ProgramProgressMondayRange | null;
  /**
   * true이면 `mondayIsoRange`가 준비될 때까지 DB/캐시 조회를 하지 않습니다.
   * 학기 경계(`termDef`)가 늦게 오는 화면에서 전량 1회 조회를 피할 때 사용합니다.
   */
  deferFetchUntilMondayRange?: boolean;
  isActive?: boolean;
}

export interface ProgramProgressCellsState {
  loading: boolean;
  error: string | null;
  cellsByAnnualKey: Record<string, ProgramProgressCell>;
  upsertCells: (rows: ProgramProgressCellUpsert[]) => Promise<void>;
}

function buildProgramProgressCellMap(rows: ProgramProgressCell[]): Record<string, ProgramProgressCell> {
  const out: Record<string, ProgramProgressCell> = {};
  rows.forEach((row) => {
    out[buildAnnualCellKey(row.grade_id, row.monday_iso, row.weekday_key, row.slot_index)] = row;
  });
  return out;
}

function replaceProgramProgressCellMapForRange(
  prev: Record<string, ProgramProgressCell>,
  rows: ProgramProgressCell[],
  range: ProgramProgressMondayRange | null
): Record<string, ProgramProgressCell> {
  const from = (range?.from ?? '').trim();
  const to = (range?.to ?? '').trim();
  const nextRowsMap = buildProgramProgressCellMap(rows);
  if (!from || !to) return nextRowsMap;
  const keptEntries = Object.entries(prev).filter(([, cell]) => {
    const mondayIso = (cell.monday_iso ?? '').trim();
    return !mondayIso || mondayIso < from || mondayIso > to;
  });
  return {
    ...Object.fromEntries(keptEntries),
    ...nextRowsMap,
  };
}

export function useProgramProgressCells(params: UseProgramProgressCellsParams): ProgramProgressCellsState {
  const localCacheDevNotedKeyRef = React.useRef<string>('');
  const lastRefreshKeyRef = React.useRef(params.refreshKey ?? 0);
  const {
    workspaceId,
    academicYear,
    termNumber,
    gradeId,
    refreshKey = 0,
    mondayIsoRange = null,
    deferFetchUntilMondayRange = false,
    isActive = true,
  } = params;
  const queryClient = useQueryClient();
  const mondayRangeFrom = (mondayIsoRange?.from ?? '').trim();
  const mondayRangeTo = (mondayIsoRange?.to ?? '').trim();
  const hasMondayRange = Boolean(mondayRangeFrom && mondayRangeTo);
  const tn = termNumber as 1 | 2;
  const queryEnabled =
    isActive && !!workspaceId && !!gradeId && !!termNumber && (!deferFetchUntilMondayRange || hasMondayRange);

  const query = useProgramProgressCellsQuery({
    workspaceId,
    academicYear,
    termNumber: tn,
    gradeId,
    enabled: queryEnabled,
    mondayIsoFrom: hasMondayRange ? mondayRangeFrom : undefined,
    mondayIsoTo: hasMondayRange ? mondayRangeTo : undefined,
  });

  React.useEffect(() => {
    if (!queryEnabled || refreshKey <= lastRefreshKeyRef.current) return;
    lastRefreshKeyRef.current = refreshKey;
    invalidateProgramProgressCellsQuery(workspaceId, academicYear, tn, gradeId);
  }, [academicYear, gradeId, queryEnabled, refreshKey, tn, workspaceId]);

  const cellsByAnnualKey = React.useMemo(() => {
    const rows = query.data ?? [];
    if (!rows.length && queryEnabled && !query.isLoading) {
      const disk = loadProgramProgressCellsCache(workspaceId, academicYear, tn, gradeId);
      if (disk.found) {
        if (hasMondayRange) {
          const sliced = filterProgramProgressCellsByMondayIsoInclusive(disk.rows, mondayRangeFrom, mondayRangeTo);
          return replaceProgramProgressCellMapForRange({}, sliced, {
            from: mondayRangeFrom,
            to: mondayRangeTo,
          });
        }
        if (disk.coverage === 'full') {
          return buildProgramProgressCellMap(disk.rows);
        }
      }
      return {};
    }
    if (hasMondayRange) {
      return buildProgramProgressCellMap(rows);
    }
    return buildProgramProgressCellMap(rows);
  }, [
    academicYear,
    gradeId,
    hasMondayRange,
    mondayRangeFrom,
    mondayRangeTo,
    query.data,
    query.isLoading,
    queryEnabled,
    tn,
    workspaceId,
  ]);

  React.useEffect(() => {
    if (!queryEnabled || query.isLoading) return;
    const devKey = `${workspaceId}|${academicYear}|${termNumber}|${gradeId}|${refreshKey}|cells-rq`;
    if (localCacheDevNotedKeyRef.current === devKey) return;
    localCacheDevNotedKeyRef.current = devKey;
    if (query.dataUpdatedAt > 0 && !query.isFetching) {
      recordDevLocalStorageProgramCacheUse(hasMondayRange ? '진도셀·구간·RQ' : '진도셀·RQ');
    }
  }, [
    academicYear,
    gradeId,
    hasMondayRange,
    query.dataUpdatedAt,
    query.isFetching,
    query.isLoading,
    queryEnabled,
    refreshKey,
    termNumber,
    workspaceId,
  ]);

  const upsertCells = React.useCallback(
    async (rows: ProgramProgressCellUpsert[]) => {
      if (!rows.length || !termNumber) return;
      try {
        const saved = await upsertProgramProgressCells(rows);
        const disk = loadProgramProgressCellsCache(workspaceId, academicYear, tn, gradeId);
        const mergedRows = disk.found
          ? [...disk.rows.filter((r) => !saved.some((s) => s.id === r.id)), ...saved]
          : saved;
        setProgramProgressCellsQueryData(workspaceId, academicYear, tn, gradeId, mergedRows);
        queryClient.setQueryData(
          schoolKeys.programProgressCells(workspaceId, academicYear, tn, gradeId),
          hasMondayRange
            ? filterProgramProgressCellsByMondayIsoInclusive(mergedRows, mondayRangeFrom, mondayRangeTo)
            : mergedRows
        );
      } catch (e) {
        throw e instanceof Error ? e : new Error(String(e));
      }
    },
    [academicYear, gradeId, hasMondayRange, mondayRangeFrom, mondayRangeTo, queryClient, termNumber, tn, workspaceId]
  );

  const loading = queryEnabled
    ? deferFetchUntilMondayRange && !hasMondayRange
      ? true
      : query.isLoading
    : false;

  return {
    loading,
    error: query.error instanceof Error ? query.error.message : query.error ? String(query.error) : null,
    cellsByAnnualKey,
    upsertCells,
  };
}
